import { call, put, select } from "redux-saga/effects";

import { consoleLogError } from "../../utils/console";
import { searchRequest } from "../../api/search/SearchAPI";
import { selectorSetting } from "../settings/selectors";
import { SETTING } from "../../utils/settings/constants";
import {
  getSuggestionsPopupRequest,
  getTextDetectedLanguagesRequest
} from "../../api/suggestions/suggestions";
import { arraymove, arrayToObjMap } from "../../utils/array";
import { selectorSearchShortcuts } from "./selectors";
import { getSearchSuggestionsRequest } from "../../api/search/suggestions/requests";
import { LOADER_ID_SUGGESTIONS } from "./constants";
import { getLanguageDefault } from "../../utils/user-agent";
import { _sharedActionsSystem } from "../system/actions";
import { _makeLangSort } from "../../utils/search/search";
import { selectorMainTree } from "../mainTree/selectors";
import { _sharedActionsSearch } from "./actions";

const scope = "Saga Search";

export function* _sharedWorkerFetchShortcuts(query) {
  if (!query) {
    return null;
  }

  const shortcutsAll = yield select(selectorSearchShortcuts);

  const queryTrimmed = query.trim();

  let responseSorted = shortcutsAll[queryTrimmed];

  if (queryTrimmed && !responseSorted) {
    try {
      const response = yield call(getSearchSuggestionsRequest, queryTrimmed, true);

      if (response) {
        // Make base bible first in the list
        responseSorted = [...response];
        const baseBible = yield select(selectorSetting(SETTING.baseBible.id));

        const indexBaseBible = responseSorted.findIndex((item) => item.bookId === baseBible);

        if (indexBaseBible !== -1 && indexBaseBible !== 0) {
          arraymove(responseSorted, indexBaseBible, 0);
        }
        // =====================================

        yield put(_sharedActionsSearch.fetchShortcutSuccess({
          ...shortcutsAll,
          [queryTrimmed]: responseSorted,
        }));
      }
    } catch (error) {
      consoleLogError("search.saga", "_sharedWorkerFetchShortcuts error: ", {
        error, query, shortcutsAll, trimQuery: queryTrimmed, responseSorted,
      });
    }
  }

  if (responseSorted?.[0]?.cutQuery) {
    const trimQuery = responseSorted?.[0]?.cutQuery.trim();
    const detectResponce = yield call(getTextDetectedLanguagesRequest, trimQuery);
    let detectMap = arrayToObjMap(detectResponce, "language", "score");

    return responseSorted.filter(
      (item) => detectMap[item.lang] !== undefined && item.hits !== 0,
    );
  }

  return responseSorted;
}

function* fetchTextDetectedLanguagesWorker(action) {
  const { query } = action.data;
  try {
    const trimQuery = query.trim();

    const detectResponce = yield call(getTextDetectedLanguagesRequest, trimQuery);
    const detectLangs = detectResponce.map((item) => item.language);
    const langScore = arrayToObjMap(detectResponce, "language", "score");

    const mainTreeLangs = yield select(selectorMainTree);
    const libraryLanguages = yield select(selectorSetting(SETTING.libraryLanguages.id));
    const languageUI = yield select(selectorSetting(SETTING.languageUI.id));

    const detectedLangs = yield _makeLangSort(
      mainTreeLangs, libraryLanguages, languageUI,
      detectLangs, langScore,
    );
    yield put(_sharedActionsSearch.fetchDetectSuccess(detectedLangs));
  } catch (error) {
    consoleLogError(scope, "fetchTextDetectedLanguagesWorker ERROR: ", error);
  }
}

export function* _sharedWorkerFetchSuggestionsPopup(action) {
  try {
    const { query, currentLang } = action.data;

    if (!query) {
      return;
    }

    const queryTrimmed = query.trim();

    if (queryTrimmed) {
      yield put(_sharedActionsSystem.loading(LOADER_ID_SUGGESTIONS));

      yield call(fetchTextDetectedLanguagesWorker, { data: { query: queryTrimmed } });

      const shortcuts = yield call(_sharedWorkerFetchShortcuts, queryTrimmed);

      const queryFinal = shortcuts?.[0]?.cutQuery || queryTrimmed;

      let correctionsFinal = [];

      // Fetches amount of results in different languages for {queryFinal}
      // Fetches a correction query for {queryFinal}
      try {
        const { searchQueryLangsCache, queryCorrectionsCache } = yield select(
          (state) => state.search,
        );

        let searchQueryLangs = searchQueryLangsCache[queryFinal];
        let correction = queryCorrectionsCache[queryFinal];

        // Doesn't check for a {correction}, because it's optional return parameter in the API.
        if (!searchQueryLangs) {
          const result = yield call(searchRequest, {
            query: queryFinal,
            start: 0,
            limit: 1,
          });
          searchQueryLangs = result?.languageFacets;
          correction = result?.suggested_query;
        }

        if (correction) {
          correctionsFinal.push(correction);
        }

        yield put(
          _sharedActionsSearch.fetchLangsForWordSuccess(searchQueryLangs, queryFinal, correction),
        );
      } catch (error) {
        consoleLogError(scope, "Try/Catch with a searchRequest ERROR: ", error);
      }
      // ============================================================

      const libraryLanguages = yield select(selectorSetting(SETTING.libraryLanguages.id));
      const defLang = getLanguageDefault(libraryLanguages);

      const responseSuggestions = yield call(
        getSuggestionsPopupRequest,
        queryFinal,
        currentLang || defLang,
      );

      let suggestionResult;

      if (responseSuggestions) {
        if (typeof responseSuggestions.did_you_mean === "string") {
          if (responseSuggestions.did_you_mean !== queryTrimmed) {
            correctionsFinal.push(responseSuggestions.did_you_mean);
          }
        } else if (
          Array.isArray(responseSuggestions.did_you_mean)
          && responseSuggestions.did_you_mean.length
        ) {
          correctionsFinal.push(
            ...responseSuggestions.did_you_mean.filter((s) => s !== queryTrimmed)
          );
        }

        const autocomplete = responseSuggestions?.autocomplete?.filter((autocompleteItem) => {
          return autocompleteItem.trim() !== queryTrimmed;
        }) ?? [];

        correctionsFinal.sort((a, b) => b.type > a.type ? 1 : -1);

        suggestionResult = {
          searchedQuery: queryTrimmed,
          corrections: correctionsFinal,
          shortcuts,
          suggestions: responseSuggestions.suggestions,
          synonyms: responseSuggestions.synonyms,
          autocomplete,
        };
      }

      yield put(_sharedActionsSearch.fetchSuggestionSuccess(suggestionResult));
    }
  } catch (error) {
    consoleLogError(scope, "_sharedWorkerFetchSuggestionsPopup ERROR: ", error);
  } finally {
    yield put(_sharedActionsSystem.loading(LOADER_ID_SUGGESTIONS, true));
  }
}
